import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import { toast } from "react-hot-toast";

import {
  CreditCard,
  Link2,
  Truck,
  RotateCcw,
  HelpCircle,
  Wallet,
  AlertTriangle,
  CircleDot,
  CircleDashed,
  CheckCircle2,
  XCircle,
  ChevronsUpDown,
  Check,
  PackageCheck,
} from "lucide-react";

import useOrders from "../../Services/OrderServices/useOrders";
import useOrdersMain from '../../Services/OrdersMainServices/useOrdersMain';


/* ---------- Small utils ---------- */

const cls = (...s) => s.filter(Boolean).join(" ");
const fmt = (n) =>
  n === undefined || n === null || n === "" || isNaN(Number(n))
    ? "0.00"
    : Number(n).toFixed(2);

const safeParse = (str) => {
  try {
    return JSON.parse(str || "{}");
  } catch {
    return {};
  }
};

const parseCustomization = (custStr) => {
  const obj = typeof custStr === "string" ? safeParse(custStr) : custStr || {};
  return Object.entries(obj);
};

const STATUSES = Array.from(
  new Set([
    "received",
    "paid",
    "accepted",
    "prepared",
    "rejected",
    "refunded",
    "driver_assigned",
    "driver_confirmed",
    "driver_pickup_arrival",
    "delivery_picked",
    "driver_dropoff_arrival",
    "completed",
    "dispatched",
    "delivered",
    "ready_for_dinning",
    "ready_for_takeway",
    "not-delivered",
  ])
).sort();

/* ---------- Badges ---------- */

const badgeTone = (kind, valueRaw) => {
  const v = String(valueRaw || "").toLowerCase();

  if (kind === "order") {
    if (
      ["completed", "delivered", "accepted", "prepared", "paid"].includes(v)
    ) {
      return {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        Icon: CheckCircle2,
      };
    }
    if (["rejected", "refunded", "not-delivered"].includes(v)) {
      return {
        bg: "bg-rose-50",
        border: "border-rose-200",
        text: "text-rose-700",
        Icon: XCircle,
      };
    }
    if (
      [
        "dispatched",
        "driver_assigned",
        "driver_confirmed",
        "driver_pickup_arrival",
        "delivery_picked",
        "driver_dropoff_arrival",
        "ready_for_dinning",
        "ready_for_takeway",
        "received",
      ].includes(v)
    ) {
      return {
        bg: "bg-sky-50",
        border: "border-sky-200",
        text: "text-sky-700",
        Icon: CircleDot,
      };
    }
    return {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-700",
      Icon: CircleDashed,
    };
  }

  if (kind === "payment") {
    if (["paid", "success", "captured"].includes(v)) {
      return {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        Icon: Wallet,
      };
    }
    if (["unpaid", "failed"].includes(v)) {
      return {
        bg: "bg-rose-50",
        border: "border-rose-200",
        text: "text-rose-700",
        Icon: XCircle,
      };
    }
    return {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
      Icon: AlertTriangle,
    };
  }

  if (kind === "delivery") {
    if (["delivered"].includes(v)) {
      return {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        Icon: PackageCheck,
      };
    }
    if (
      [
        "dispatched",
        "driver_assigned",
        "driver_confirmed",
        "driver_pickup_arrival",
        "delivery_picked",
        "driver_dropoff_arrival",
      ].includes(v)
    ) {
      return {
        bg: "bg-indigo-50",
        border: "border-indigo-200",
        text: "text-indigo-700",
        Icon: Truck,
      };
    }
    if (["not-delivered"].includes(v)) {
      return {
        bg: "bg-rose-50",
        border: "border-rose-200",
        text: "text-rose-700",
        Icon: XCircle,
      };
    }
    return {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-700",
      Icon: CircleDashed,
    };
  }

  return {
    bg: "bg-gray-50",
    border: "border-gray-200",
    text: "text-gray-700",
    Icon: CircleDashed,
  };
};

function StatusBadge({ kind, value, extra }) {
  const { bg, border, text, Icon } = badgeTone(kind, value);
  return (
    <span
      className={cls(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        bg,
        border,
        text
      )}
    >
      <Icon size={13} />
      <span className="capitalize">{value || "—"}</span>
      {extra ? <span className="opacity-70">({extra})</span> : null}
    </span>
  );
}

/* ---------- Generic Dialogs (Confirm + Select) ---------- */

function ConfirmDialog({
  open,
  onClose,
  title = "Confirm",
  body,
  confirmText = "Confirm",
  tone = "primary",
  busy = false,
  confirmDisabled = false,
  onConfirm,
}) {
  const btnTone =
    tone === "danger"
      ? "bg-rose-600 hover:bg-rose-700"
      : tone === "success"
        ? "bg-emerald-600 hover:bg-emerald-700"
        : "bg-sky-600 hover:bg-sky-700";

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        onClose={() => (!busy && onClose ? onClose() : null)}
        className="relative z-[60]"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <Dialog.Title className="text-base font-semibold">
                {title}
              </Dialog.Title>
              <div className="mt-2 text-sm text-gray-700">{body}</div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="h-9 rounded-xl border px-3 text-sm"
                  onClick={onClose}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className={cls(
                    "h-9 rounded-xl px-4 text-sm font-medium text-white",
                    btnTone,
                    (busy || confirmDisabled) && "opacity-60 cursor-not-allowed"
                  )}
                  onClick={onConfirm}
                  disabled={busy || confirmDisabled}
                >
                  {busy ? "Working…" : confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function SelectDialog({
  open,
  onClose,
  title = "Select",
  options = [],
  selected,
  setSelected,
  confirmText = "Apply",
  busy = false,
  onConfirm,
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        onClose={() => (!busy && onClose ? onClose() : null)}
        className="relative z-[60]"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <Dialog.Title className="mb-2 text-base font-semibold">
                {title}
              </Dialog.Title>
              <Listbox value={selected} onChange={setSelected}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-xl border bg-white py-2 pl-3 pr-10 text-left text-sm">
                    <span className="block truncate">
                      {selected?.label || "Select"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronsUpDown className="h-4 w-4 opacity-60" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border bg-white py-1 text-sm shadow-lg">
                      {options.map((opt) => (
                        <Listbox.Option
                          key={opt.value}
                          value={opt}
                          className={({ active }) =>
                            cls(
                              "relative cursor-default select-none px-3 py-2",
                              active && "bg-gray-50"
                            )
                          }
                        >
                          {({ selected }) => (
                            <div className="flex items-center gap-2">
                              <span
                                className={cls(
                                  "grid h-5 w-5 place-items-center rounded border",
                                  selected
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                    : "text-transparent"
                                )}
                              >
                                <Check size={14} />
                              </span>
                              <span
                                className={cls(
                                  "block truncate",
                                  selected && "font-medium"
                                )}
                              >
                                {opt.label}
                              </span>
                            </div>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="h-9 rounded-xl border px-3 text-sm"
                  onClick={onClose}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="h-9 rounded-xl bg-amber-600 px-4 text-sm font-medium text-white hover:bg-amber-700"
                  onClick={onConfirm}
                  disabled={busy}
                >
                  {busy ? "Working…" : confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

/* ---------- Main Modal ---------- */

export default function OrderDetailsModal({ open, onClose, orderId, api }) {
  const { getOrderDetails, updateOrderPaymentStatus, generateOrderPaymentIntent, assignDeliveryPartner, processRefund, easebuzzRefund, requestCustomerAddress } = useOrders();
  const { changeCatalogOrderStatus } = useOrdersMain();


  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusValue, setStatusValue] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCfg, setConfirmCfg] = useState({
    title: "",
    body: "",
    tone: "primary",
    onConfirm: null,
    busy: false,
    confirmDisabled: false,
  });
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectCfg, setSelectCfg] = useState({
    title: "",
    options: [],
    selected: null,
    onConfirm: null,
    busy: false,
  });

  const customizationPairs = useMemo(
    () =>
      (details?.items || []).map((it) =>
        parseCustomization(it.customization || "{}")
      ),
    [details]
  );

  /* ---------- Fetch order details when opened ---------- */

  useEffect(() => {
    if (!open || !orderId) return;

    const loadDetails = async () => {
      setBusy(true);

      const result = await getOrderDetails(orderId);
      if (result) {

        setDetails(result);
        setStatusValue(result.status || "");
      } else {
        setDetails(null);
        toast.error(result.message);
      }

      setBusy(false);
    };

    loadDetails();
  }, [open, orderId]);

  const clientUserId =
    details?.fk_user_id || details?.us_code || details?.userid || "";

  /* ---------- Confirm helpers ---------- */

  const confirmAction = ({
    title,
    body,
    tone = "primary",
    confirmDisabled = false,
    onConfirm,
  }) => {
    setConfirmCfg({
      title,
      body,
      tone,
      busy: false,
      confirmDisabled,
      onConfirm: async () => {
        setConfirmCfg((c) => ({ ...c, busy: true }));
        await onConfirm?.();
        setConfirmCfg((c) => ({ ...c, busy: false }));
        setConfirmOpen(false);
      },
    });
    setConfirmOpen(true);
  };
  /* ---------- Actions ---------- */

  const refetchDetails = async () => {
    if (!orderId) return;

    const result = await getOrderDetails(orderId);

    if (result && Object.keys(result).length > 0) {
      setDetails(result);
      setStatusValue(result?.status || "");
    }
  };

  const updatePaymentStatus = () => {
    if (!details?.orderid) return;

    confirmAction({
      title: "Update Payment Status?",
      body: "You are about to update the payment status for this order.",
      onConfirm: async () => {
        const result = await updateOrderPaymentStatus(details.orderid);

        if (result.success) {
          const d = result.data;
          toast.success(`Payment updated: ${d.status} • Ref: ${d.refid}`);
          await refetchDetails();
        } else {
          toast.error(result.message);
        }
      },
    });
  };

  const generatePaymentIntent = () => {
    if (!details?.orderid) return;

    const payload = {
      userid: clientUserId,
      orderId: details.orderid,
      custName: details.customer_name || "N/A",
      custMobile: details.customer_mobile || "",
    };

    confirmAction({
      title: "Send Payment Link?",
      body: (
        <div className="text-sm">
          <div>
            <b>User ID:</b> {payload.userid || "N/A"}
          </div>
          <div>
            <b>Order ID:</b> {payload.orderId}
          </div>
          <div>
            <b>Customer:</b> {payload.custName}
          </div>
          <div>
            <b>Mobile:</b> {payload.custMobile || "N/A"}
          </div>
        </div>
      ),
      onConfirm: async () => {
        const result = await generateOrderPaymentIntent(payload);

        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      },
    });
  };

  const openSendToDeliveryPartner = () => {
    if (!details?.orderid) return;

    const options = [
      { value: "", label: "None (Prepaid/Null)" },
      { value: "COD", label: "Cash on Delivery" },
    ];

    const [first] = options;

    setSelectCfg({
      title: "Send to Delivery Partner",
      options,
      selected: first,
      busy: false,
      onConfirm: async () => {
        setSelectCfg((c) => ({ ...c, busy: true }));

        const payload = {
          userid: clientUserId,
          orderId: details.orderid,
          platform: "whatsapp",
          paymentMode: selectCfg.selected?.value || null,
          deliveryPartner: "packky",
        };

        const result = await assignDeliveryPartner(payload);

        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }

        setSelectCfg((c) => ({ ...c, busy: false }));
        setSelectOpen(false);
        await refetchDetails();
      },
    });

    setSelectOpen(true);
  };

  const refundPayment = () => {
    if (!details) return;

    const amount = Math.trunc(
      Number(details?.pay_amount ?? details?.totalOrderPrice ?? 0)
    ).toString();

    const transactionId = String(details?.virtual_acc || "");

    if (!transactionId) {
      toast.error("Missing transaction id (virtual account).");
      return;
    }

    let remarkValue = "";

    confirmAction({
      title: "Confirm Refund",
      tone: "danger",
      confirmDisabled: true,
      body: (
        <div className="text-sm space-y-3">
          <div>
            <b>Transaction ID:</b> {transactionId}
          </div>
          <div>
            <b>Amount:</b> ₹{amount}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Remark *</label>
            <textarea
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
              rows={3}
              placeholder="Enter remark (required)"
              onChange={(e) => {
                const v = e.target.value || "";
                remarkValue = v;

                setConfirmCfg((c) => ({
                  ...c,
                  confirmDisabled: v.trim().length === 0,
                }));
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              Remark is required to proceed.
            </div>
          </div>
        </div>
      ),
      onConfirm: async () => {
        const remark = (remarkValue || "").trim();

        if (!remark) {
          toast.error("Please enter a remark.");
          return;
        }

        const result = await processRefund({
          userid: clientUserId || "",
          amount,
          transactionId,
          remark,
        });

        if (result.success) {
          toast.success(result.message);
          await refetchDetails();
        } else {
          toast.error(result.message);
        }
      },
    });
  };
  const refundEasebuzzPayment = (orderObj) => {
    if (!orderObj?.orderid) {
      toast.error("Missing order id.");
      return;
    }

    const maxAmount = Number(orderObj?.pay_amount ?? 0);

    if (!Number.isFinite(maxAmount) || maxAmount <= 0) {
      toast.error("Invalid order amount.");
      return;
    }

    let refundAmount = maxAmount.toFixed(2);
    let remarkValue = "";

    confirmAction({
      title: "Confirm Easebuzz Refund",
      tone: "danger",
      confirmDisabled: true,
      body: (
        <div className="text-sm space-y-3">
          <div>
            <b>Order ID:</b> {orderObj.orderid}
          </div>

          <div>
            <b>Max Refundable Amount:</b> ₹{maxAmount.toFixed(2)}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Refund Amount *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              defaultValue={refundAmount}
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="Enter refund amount"
              onChange={(e) => {
                refundAmount = e.target.value || "";

                const amt = Number(refundAmount);
                const remarkOk = (remarkValue || "").trim().length > 0;
                const amountOk =
                  refundAmount !== "" &&
                  Number.isFinite(amt) &&
                  amt > 0 &&
                  amt <= maxAmount;

                setConfirmCfg((c) => ({
                  ...c,
                  confirmDisabled: !(remarkOk && amountOk),
                }));
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              Refund amount must be greater than 0 and cannot exceed ₹{maxAmount.toFixed(2)}.
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Remark *</label>
            <textarea
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              rows={3}
              placeholder="Enter remark (required)"
              onChange={(e) => {
                remarkValue = e.target.value || "";

                const amt = Number(refundAmount);
                const remarkOk = remarkValue.trim().length > 0;
                const amountOk =
                  refundAmount !== "" &&
                  Number.isFinite(amt) &&
                  amt > 0 &&
                  amt <= maxAmount;

                setConfirmCfg((c) => ({
                  ...c,
                  confirmDisabled: !(remarkOk && amountOk),
                }));
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              Remark is required to proceed.
            </div>
          </div>
        </div>
      ),
      onConfirm: async () => {
        const amountNum = Number(refundAmount);
        const remark = (remarkValue || "").trim();

        if (!remark) {
          toast.error("Please enter a remark.");
          return;
        }

        if (!refundAmount || !Number.isFinite(amountNum) || amountNum <= 0) {
          toast.error("Please enter a valid refund amount.");
          return;
        }

        if (amountNum > maxAmount) {
          toast.error(`Refund amount cannot exceed ₹${maxAmount.toFixed(2)}.`);
          return;
        }

        const payload = {
          orderId: orderObj.orderid,
          amount: amountNum.toFixed(2),
          remark,
        };

        const result = await easebuzzRefund(payload);

        if (result.success) {
          toast.success(result.message || "Easebuzz refund initiated");
          await refetchDetails();
        } else {
          toast.error(result.message || "Easebuzz refund failed");
        }
      },
    });
  };
  const requestAddress = () => {
    if (!details?.orderid) return;

    confirmAction({
      title: "Request Customer Address?",
      body: "Send a WhatsApp address request for this order.",
      onConfirm: async () => {
        const result = await requestCustomerAddress(details.orderid);

        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      },
    });
  };

  const confirmChangeOrderStatus = () => {
    if (!details?.orderid) return;

    if (!statusValue) {
      toast.error("Select a status first");
      return;
    }

    if (!clientUserId) {
      toast.error("Client id (userid) missing");
      return;
    }

    confirmAction({
      title: "Change Order Status?",
      body: (
        <>
          Set status to{" "}
          <b className="capitalize">{statusValue || "(none)"}</b> for order{" "}
          <b>#{details.orderid}</b>?
        </>
      ),
      onConfirm: async () => {
        setBusy(true);

        const result = await changeCatalogOrderStatus({
          userid: clientUserId,
          orderid: details.orderid,
          productid: "",
          status: statusValue,
          time: "",
          payment: "",
        });

        if (result.success) {
          toast.success(`Order updated → ${statusValue}`);
          await refetchDetails();
        } else {
          toast.error(result.message);
        }

        setBusy(false);
      },
    });
  };

  /* ---------- Render ---------- */

  return (
    <>
      <Transition show={open} as={Fragment}>
        <Dialog
          onClose={() => (!busy && onClose ? onClose() : null)}
          className="relative z-50"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <Dialog.Title className="text-base font-semibold">
                    Order Details
                  </Dialog.Title>
                  <button
                    className="h-8 rounded-xl border px-3 text-sm"
                    onClick={onClose}
                    disabled={busy}
                  >
                    Close
                  </button>
                </div>

                {/* Top actions bar */}
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border bg-gray-50 p-2">
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700"
                    onClick={updatePaymentStatus}
                    disabled={!details}
                  >
                    <CreditCard size={14} /> Update Payment
                  </button>
                  {/* <button
                    className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
                    onClick={generatePaymentIntent}
                    disabled={!details}
                  >
                    <Link2 size={14} /> {c.us_name} : {c.us_code}
                  </button> */}
                  {details?.orderType === "home_delivery" && (
                    <button
                      className="inline-flex items-center gap-1 rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                      onClick={openSendToDeliveryPartner}
                      disabled={!details}
                    >
                      <Truck size={14} /> Send to DP
                    </button>
                  )}
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                    onClick={refundPayment}
                    disabled={
                      !details ||
                      String(details?.payment_status || "")
                        .toLowerCase()
                        .includes("unpaid") ||
                      !details?.virtual_acc ||
                      details?.refund_status === "Y"
                    }
                  >
                    <RotateCcw size={14} /> Refund Pay
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => refundEasebuzzPayment(details)}
                    disabled={
                      !details ||
                      String(details?.payment_status || "").toLowerCase() === "unpaid" ||
                      details?.refund_status === "Y" ||
                      Number(details?.pay_amount || 0) <= 0 ||
                      !details?.orderid
                    }
                  >
                    <RotateCcw size={14} /> Refund EBuzz
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700"
                    onClick={requestAddress}
                    disabled={!details}
                  >
                    <HelpCircle size={14} /> Request Address
                  </button>
                </div>

                <div className="max-h-[70vh] space-y-3 overflow-auto text-sm">
                  {busy && !details ? (
                    <div className="py-6 text-center text-gray-500">
                      Loading details…
                    </div>
                  ) : !details ? (
                    <div className="py-6 text-center text-gray-500">
                      No details available.
                    </div>
                  ) : (
                    <>
                      {/* Header badges */}
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border p-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate text-sm font-semibold">
                              Order #{details.orderid}
                            </h3>
                            <StatusBadge
                              kind="order"
                              value={details.status}
                            />
                            <StatusBadge
                              kind="order"
                              value={details.orderType}
                            />
                            <StatusBadge
                              kind="payment"
                              value={details.payment_status}
                              extra={details.payment_mode}
                            />
                            {details.delivery_partner_status && (
                              <StatusBadge
                                kind="delivery"
                                value={details.delivery_partner_status}
                              />
                            )}
                            {details.refund_status === "Y" && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-600/10 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                                <RotateCcw size={13} /> Refunded
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            Order: {details.order_date || "N/A"} · Updated:{" "}
                            {details.update || "N/A"}
                          </div>
                        </div>

                        {/* Status change controls */}
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            className="h-9 rounded-xl border bg-white px-2 text-xs outline-none"
                            value={statusValue}
                            onChange={(e) => setStatusValue(e.target.value)}
                          >
                            <option value="">Select status</option>
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                          <button
                            className="h-9 rounded-xl bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
                            onClick={confirmChangeOrderStatus}
                            disabled={!statusValue || busy}
                          >
                            {busy ? "Updating…" : "Update Status"}
                          </button>
                        </div>
                      </div>

                      {/* Summary + Customer/Address */}
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-2xl border p-3">
                          <div className="mb-2 text-sm font-semibold">
                            Summary
                          </div>
                          <div>
                            <h3>{details.us_name || "N/A"}</h3>
                          </div>
                          <div>
                            <b>Total:</b> ₹{fmt(details.totalOrderPrice)}
                          </div>
                          <div>
                            <b>Subtotal:</b> ₹{fmt(details.subtotal)}
                          </div>
                          <div>
                            <b>Platform:</b> {details.platform || "N/A"}
                          </div>
                          <div>
                            <b>Virtual Acc:</b> {details.virtual_acc || "N/A"}
                          </div>
                          <div>
                            <b>GST Claim:</b> {details.gst_claim || "N/A"}
                          </div>
                          <div>
                            <b>Loyalty Points:</b>{" "}
                            {details.loyalty_points || "0"}
                          </div>
                        </div>
                        <div className="rounded-2xl border p-3">
                          <div className="mb-2 text-sm font-semibold">
                            Customer & Address
                          </div>
                          <div>
                            <b>Name:</b> {details.customer_name || "N/A"}
                          </div>
                          <div>
                            <b>Phone:</b> {details.customer_mobile || "N/A"}
                          </div>
                          <div className="text-xs text-gray-600">
                            {details.address_line}
                          </div>
                          {(() => {
                            const a =
                              safeParse(details?.address_json).values || {};
                            return (
                              <div className="text-xs text-gray-600">
                                {a?.address || ""} {a?.landmark_area || ""}{" "}
                                {a?.building_name || ""}{" "}
                                {a?.floor_number || ""} {a?.tower_number || ""}{" "}
                                {a?.city || ""} {a?.state || ""}{" "}
                                {a?.in_pin_code || ""}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Items table */}
                      <div className="overflow-auto rounded-2xl border">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="text-left">
                              {[
                                "#",
                                "Product",
                                "Qty",
                                "Item Price",
                                "Total",
                                "Customization",
                              ].map((h) => (
                                <th key={h} className="px-3 py-2">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(details.items || []).map((it, i) => {
                              const ps = customizationPairs[i] || [];
                              return (
                                <tr
                                  key={`${it.id || it.product_id}-${i}`}
                                  className="border-t align-top"
                                >
                                  <td className="px-3 py-2">{i + 1}</td>
                                  <td className="px-3 py-2">
                                    {it.product_id || it.item_name || "—"}
                                  </td>
                                  <td className="px-3 py-2">
                                    {it.quantity ?? "—"}
                                  </td>
                                  <td className="px-3 py-2">
                                    ₹{fmt(it.item_price)}
                                  </td>
                                  <td className="px-3 py-2">
                                    ₹{fmt(it.total_price)}
                                  </td>
                                  <td className="px-3 py-2">
                                    {ps.length === 0 ? (
                                      <span className="text-gray-400">—</span>
                                    ) : (
                                      <div className="flex flex-wrap gap-2">
                                        {ps.map(([k, v]) => (
                                          <span
                                            key={`${k}-${v}-${i}`}
                                            className="rounded-full border bg-gray-50 px-2 py-0.5 text-xs"
                                          >
                                            <b>{k}:</b> {String(v)}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {(details.items || []).length === 0 && (
                              <tr>
                                <td
                                  className="px-3 py-4 text-center text-gray-500"
                                  colSpan={6}
                                >
                                  No items
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Shared small dialogs */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={confirmCfg.title}
        body={confirmCfg.body}
        confirmText={
          confirmCfg.tone === "danger" ? "Yes, confirm" : "Confirm"
        }
        tone={confirmCfg.tone}
        busy={confirmCfg.busy}
        confirmDisabled={confirmCfg.confirmDisabled}
        onConfirm={confirmCfg.onConfirm}
      />
      <SelectDialog
        open={selectOpen}
        onClose={() => setSelectOpen(false)}
        title={selectCfg.title}
        options={selectCfg.options}
        selected={selectCfg.selected}
        setSelected={(v) =>
          setSelectCfg((c) => ({
            ...c,
            selected: v,
          }))
        }
        confirmText="Send"
        busy={selectCfg.busy}
        onConfirm={selectCfg.onConfirm}
      />
    </>
  );
}
